import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axios";
import { toast } from "react-toastify";

const StatCard = ({ label, value }) => (
  <div className="rounded-lg border border-border/70 bg-card p-5">
    <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1">
      {label}
    </p>
    <p className="font-mono text-xl text-foreground">{value ?? 0}</p>
  </div>
);

const thCls = "border-b border-border/70 px-5 py-3 text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac] text-left";
const tdCls = "border-b border-border/40 px-5 py-3.5 text-sm text-foreground";

const statusBadge = (status) => {
  const cls =
    status === "active"
      ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
      : status === "expired"
      ? "bg-red-500/15 text-red-400 border border-red-500/20"
      : "bg-white/[0.05] text-muted-foreground border border-border/50";
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
};

const IPVPNStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/admin/services/ipvpn-stats");
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch IPVPN stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="p-4">
        <div className="text-center text-sm text-muted-foreground py-12">Loading stats...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-4">
        <div className="text-center text-sm text-muted-foreground py-12">No stats available</div>
      </div>
    );
  }

  const { overall, byPlan, byDevices, byWalletType, byStatusAndPlan } = stats;

  return (
    <div className="p-4">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Services
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        IPVPN — <span className="text-gradient-ginox">Stats</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        Aggregated statistics for IPVPN subscriptions.
      </p>

      {/* Overall */}
      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
          Overall
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard label="Total Purchased" value={overall.totalPurchased} />
          <StatCard label="Amount Spent" value={overall.totalAmountSpent} />
          <StatCard label="Total Free" value={overall.totalFree} />
          <StatCard label="Total Paid" value={overall.totalPaid} />
          <StatCard label="Active" value={overall.totalActive} />
          <StatCard label="Expired" value={overall.totalExpired} />
          <StatCard label="Unique Users" value={overall.uniqueUsers} />
        </div>
      </div>

      {/* By Plan */}
      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
          By Plan
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className={thCls}>Plan</th>
                <th className={thCls}>Count</th>
                <th className={thCls}>Amount Spent</th>
                <th className={thCls}>Free</th>
                <th className={thCls}>Paid</th>
                <th className={thCls}>Active</th>
                <th className={thCls}>Expired</th>
              </tr>
            </thead>
            <tbody>
              {byPlan.map((p) => (
                <tr key={p.planId} className="group hover:bg-white/[0.02] transition-colors">
                  <td className={tdCls}>{p.planName}</td>
                  <td className={`${tdCls} font-mono`}>{p.count}</td>
                  <td className={`${tdCls} font-mono`}>{p.amountSpent}</td>
                  <td className={`${tdCls} font-mono`}>{p.freeCount}</td>
                  <td className={`${tdCls} font-mono`}>{p.paidCount}</td>
                  <td className={`${tdCls} font-mono`}>{p.activeCount}</td>
                  <td className={`${tdCls} font-mono`}>{p.expiredCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* By Devices */}
      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
          By Devices
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className={thCls}>Devices</th>
                <th className={thCls}>Count</th>
                <th className={thCls}>Amount Spent</th>
                <th className={thCls}>Active</th>
                <th className={thCls}>Expired</th>
              </tr>
            </thead>
            <tbody>
              {byDevices.map((d) => (
                <tr key={d.deviceId} className="group hover:bg-white/[0.02] transition-colors">
                  <td className={tdCls}>{d.deviceName}</td>
                  <td className={`${tdCls} font-mono`}>{d.count}</td>
                  <td className={`${tdCls} font-mono`}>{d.amountSpent}</td>
                  <td className={`${tdCls} font-mono`}>{d.activeCount}</td>
                  <td className={`${tdCls} font-mono`}>{d.expiredCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* By Wallet Type */}
      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
          By Wallet Type
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className={thCls}>Wallet Type</th>
                <th className={thCls}>Count</th>
                <th className={thCls}>Amount Spent</th>
              </tr>
            </thead>
            <tbody>
              {byWalletType.map((w) => (
                <tr key={w.walletType} className="group hover:bg-white/[0.02] transition-colors">
                  <td className={tdCls}>{w.walletType}</td>
                  <td className={`${tdCls} font-mono`}>{w.count}</td>
                  <td className={`${tdCls} font-mono`}>{w.amountSpent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* By Status and Plan */}
      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
          By Status &amp; Plan
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className={thCls}>Status</th>
                <th className={thCls}>Plan</th>
                <th className={thCls}>Count</th>
              </tr>
            </thead>
            <tbody>
              {byStatusAndPlan.map((s, i) => (
                <tr key={`${s.status}-${s.planId}-${i}`} className="group hover:bg-white/[0.02] transition-colors">
                  <td className={tdCls}>{statusBadge(s.status)}</td>
                  <td className={tdCls}>{s.planName}</td>
                  <td className={`${tdCls} font-mono`}>{s.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default IPVPNStats;
